import { useState } from 'react';
import {
  Plus,
  Bell,
  Clock,
  Star,
  Trash2,
  ChevronRight,
  Shield,
  AlertTriangle,
  TrendingUp,
} from 'react-feather';
import { DillButton } from '../../../general/Dill';
import {
  CATEGORIES,
  COLOR_MAP,
  CAT_ORDER,
  FREQUENCY_COLORS,
  LANGUAGE_OPTIONS,
  getRuleCategory,
  getPortfolioHealth,
} from './portfolioTypes';
import type { CreditSafePortfolio, CategoryKey, ColorType } from './portfolioTypes';

// ─────────────────────────────────────────────
// PROPS
// ─────────────────────────────────────────────

export type PortfolioListProps = {
  portfolios: CreditSafePortfolio[];
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
  onNewPortfolio: () => void;
};

// ─────────────────────────────────────────────
// RULE CATEGORY PILLS
// ─────────────────────────────────────────────

function RuleCategoryPills({ rules }: { rules: CreditSafePortfolio['rules'] }) {
  const activeRules = (rules ?? []).filter((r: any) => r.isActive === 1);
  if (activeRules.length === 0) return null;

  const counts: Partial<Record<CategoryKey, number>> = {};

  for (const r of activeRules) {
    const cat = getRuleCategory(r.ruleCode) as CategoryKey;
    counts[cat] = (counts[cat] ?? 0) + 1;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {CAT_ORDER.filter((c: CategoryKey) => counts[c]).map((c: CategoryKey) => {
        const cat = CATEGORIES[c];
        const colors = COLOR_MAP[cat.color as ColorType];

        return (
          <span
            key={c}
            className={`inline-flex items-center gap-0.5 text-[10px] font-semibold rounded-md px-1.5 py-0.5 ${colors.pill}`}>
            {cat.icon} {counts[c]}
          </span>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────
// RECIPIENT AVATARS
// ─────────────────────────────────────────────

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-violet-500',
  'bg-emerald-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-cyan-500',
];

function RecipientAvatars({ emails }: { emails: CreditSafePortfolio['emails'] }) {
  if (emails.length === 0) return null;
  const shown = emails.slice(0, 4);
  const extra = emails.length - 4;
  return (
    <div className="flex items-center -space-x-2">
      {shown.map((e: any, i: number) => (
        <div
          key={i}
          title={`${e.firstName} ${e.lastName}`}
          className={`w-6 h-6 rounded-full ${
            AVATAR_COLORS[i % AVATAR_COLORS.length]
          } border-2 border-white flex items-center justify-center flex-shrink-0`}>
          <span className="text-[9px] font-bold text-white leading-none">
            {e.firstName[0]}
            {e.lastName[0]}
          </span>
        </div>
      ))}
      {extra > 0 && (
        <div className="w-6 h-6 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center flex-shrink-0">
          <span className="text-[9px] font-bold text-gray-600">+{extra}</span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// HEALTH INDICATOR
// ─────────────────────────────────────────────

function HealthDot({ health }: { health: 'healthy' | 'warning' | 'empty' }) {
  const styles = {
    healthy: 'bg-emerald-400',
    warning: 'bg-amber-400',
    empty: 'bg-gray-300',
  };
  return <span className={`w-2 h-2 rounded-full flex-shrink-0 ${styles[health]}`} />;
}

// ─────────────────────────────────────────────
// DELETE CONFIRM POPOVER
// ─────────────────────────────────────────────

function DeleteConfirm({
  name,
  onConfirm,
  onCancel,
}: {
  name: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/95 backdrop-blur-sm rounded-2xl p-5 text-center"
      onClick={(e) => e.stopPropagation()}>
      <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mb-3">
        <Trash2 size={16} className="text-red-500" />
      </div>
      <p className="text-sm font-bold text-gray-900 mb-1">Remove portfolio?</p>
      <p className="text-xs text-gray-500 mb-4 leading-relaxed">
        <span className="font-medium text-gray-700">"{name}"</span> and all its rule settings will
        be permanently removed.
      </p>
      <div className="flex gap-2 w-full">
        <button
          onClick={onCancel}
          className="flex-1 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg py-2 hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="flex-1 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg py-2 transition-colors">
          Remove
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// PORTFOLIO CARD
// ─────────────────────────────────────────────

function PortfolioCard({
  portfolio,
  onSelect,
  onRemove,
}: {
  portfolio: CreditSafePortfolio;
  onSelect: () => void;
  onRemove: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const rules = portfolio.rules ?? [];
  const activeCount = rules.filter((r: any) => r.isActive === 1).length;
  const totalCount = rules.length;
  const health = getPortfolioHealth(portfolio);
  const freqColor =
    FREQUENCY_COLORS[portfolio.frequency] ?? 'bg-gray-100 text-gray-600 border-gray-200';
  const langLabel =
    LANGUAGE_OPTIONS.find((l: any) => l.value === portfolio.emailLanguage)?.label ??
    portfolio.emailLanguage;

  // Determine dominant category for accent bar
  const counts: Partial<Record<CategoryKey, number>> = {};
  for (const r of rules.filter((r2: any) => r2.isActive === 1)) {
    const c = getRuleCategory(r.ruleCode);
    counts[c] = (counts[c] ?? 0) + 1;
  }
  const dominantCat: CategoryKey = CAT_ORDER.find((c: CategoryKey) => counts[c]) ?? 'company_info';
  const accentColor = COLOR_MAP[CATEGORIES[dominantCat].color as ColorType].bar;

  return (
    <div
      onClick={() => !confirmDelete && onSelect()}
      className="relative bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg hover:shadow-gray-100/80 transition-all duration-200 cursor-pointer group overflow-hidden flex flex-col">
      {/* Top accent bar */}
      <div className={`h-1 w-full ${activeCount > 0 ? accentColor : 'bg-gray-100'}`} />

      {confirmDelete && (
        <DeleteConfirm
          name={portfolio.name}
          onConfirm={() => {
            setConfirmDelete(false);
            onRemove();
          }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}

      <div className="p-5 flex-1 flex flex-col gap-4">
        {/* Header row */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <HealthDot health={health} />
              <h3 className="text-sm font-bold text-gray-900 truncate">{portfolio.name}</h3>
              {portfolio.isDefault && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold bg-amber-50 text-amber-600 border border-amber-200 rounded-full px-1.5 py-0.5 flex-shrink-0">
                  <Star size={8} fill="currentColor" /> Default
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`text-[11px] font-medium border rounded-full px-2 py-0.5 ${freqColor}`}>
                <Clock size={9} className="inline mr-0.5" />
                {portfolio.frequency}
              </span>
              <span className="text-[11px] text-gray-400">{langLabel}</span>
            </div>
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setConfirmDelete(true);
            }}
            className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-300 hover:text-red-400 hover:bg-red-50 rounded-lg transition-all flex-shrink-0">
            <Trash2 size={13} />
          </button>
        </div>

        {/* Rule stats */}
        <div className="flex flex-col gap-2">
          {activeCount > 0 ? (
            <>
              <RuleCategoryPills rules={portfolio.rules} />
              <div className="flex items-center gap-1.5">
                <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${accentColor} rounded-full transition-all`}
                    style={{
                      width: totalCount > 0 ? `${(activeCount / totalCount) * 100}%` : '0%',
                    }}
                  />
                </div>
                <span className="text-[10px] text-gray-400 flex-shrink-0">
                  {activeCount}/{totalCount} active
                </span>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
              <Bell size={11} className="text-gray-300" />
              <span className="italic">No monitoring rules</span>
            </div>
          )}
        </div>

        {/* Footer: recipients + warnings */}
        <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-50">
          {portfolio.emails.length > 0 ? (
            <RecipientAvatars emails={portfolio.emails} />
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] text-amber-500">
              <AlertTriangle size={10} /> No recipients
            </span>
          )}
          <div className="flex items-center gap-1 text-[12px] text-gray-400 group-hover:text-blue-500 transition-colors">
            <span>View</span>
            <ChevronRight size={12} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// STATS BAR
// ─────────────────────────────────────────────

function StatsBar({ portfolios }: { portfolios: CreditSafePortfolio[] }) {
  const totalActive = portfolios.reduce(
    (a, p) => a + (p.rules ?? []).filter((r: any) => r.isActive === 1).length,
    0
  );
  const noRecipients = portfolios.filter((p) => p.emails.length === 0).length;

  return (
    <div className="grid grid-cols-3 gap-3 mb-6">
      {[
        {
          label: 'Total Portfolios',
          value: portfolios.length,
          icon: <Shield size={14} className="text-blue-500" />,
          color: 'text-gray-900',
        },
        {
          label: 'Active Rules',
          value: totalActive,
          icon: <TrendingUp size={14} className="text-emerald-500" />,
          color: 'text-gray-900',
        },
        {
          label: 'Need Attention',
          value: noRecipients,
          icon: <AlertTriangle size={14} className="text-amber-500" />,
          color: noRecipients > 0 ? 'text-amber-600' : 'text-gray-900',
        },
      ].map(({ label, value, icon, color }) => (
        <div
          key={label}
          className="bg-white border border-gray-100 rounded-xl px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
            {icon}
          </div>
          <div>
            <p className={`text-lg font-bold leading-none ${color}`}>{value}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">{label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// PORTFOLIO LIST
// ─────────────────────────────────────────────

export default function PortfolioList({
  portfolios,
  onSelect,
  onRemove,
  onNewPortfolio,
}: PortfolioListProps) {
  return (
    <div className="bg-gray-50/70">
      <div className="w-full">
        {/* Page header */}
        <div className="flex items-end justify-between mb-4">
          <p className="text-sm font-medium font-semibold mb-1">Portfolios</p>
          <DillButton onClick={onNewPortfolio} label="New Portfolio" icon={<Plus size={14} />} />
        </div>

        <div className="space-y-4 h-full overflow-y-auto">
          {/* Stats */}
          <StatsBar portfolios={portfolios} />

          {/* Grid */}
          {portfolios.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
                <Shield size={28} className="text-blue-400" />
              </div>
              <p className="text-base font-bold text-gray-700 mb-2">No portfolios yet</p>
              <p className="text-sm text-gray-400 mb-5 max-w-xs">
                Create your first portfolio to start monitoring companies and receiving credit risk
                alerts.
              </p>
              <DillButton
                onClick={onNewPortfolio}
                label="Create Portfolio"
                icon={<Plus size={14} />}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {portfolios.map((p) => (
                <PortfolioCard
                  key={p.id}
                  portfolio={p}
                  onSelect={() => onSelect(p.id)}
                  onRemove={() => onRemove(p.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
